import React, { useState } from "react";
import { Plane, MapPin, Calendar, ArrowLeftRight, Search, Clock, Users, ArrowRight, CheckCircle2, Luggage, Utensils } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { FLIGHTS } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const airlineColor = {
  "6E": "bg-indigo-600 text-white",       // IndiGo (dark navy blue)
  AI: "bg-red-600 text-white",             // Air India (Maharaja red)
  UK: "bg-violet-700 text-white",          // Vistara (aubergine)
  SG: "bg-red-500 text-white",             // SpiceJet (red/orange)
  G8: "bg-sky-600 text-white",             // Go First (sky blue)
};

const FlightSearch = () => {
  const { toast } = useToast();
  const today = new Date().toISOString().slice(0, 10);

  const [q, setQ] = useState({ from: "", to: "", date: today, pax: 1, trip: "oneway" });
  const [results, setResults] = useState(FLIGHTS);
  const [selected, setSelected] = useState(null);
  const [traveller, setTraveller] = useState({ name: "", phone: "", email: "" });
  const [confirmed, setConfirmed] = useState(null);

  const search = (e) => {
    e.preventDefault();
    if (!q.from || !q.to) {
      toast({ title: "Please add cities", description: "Enter both origin and destination." });
      return;
    }
    const term = (s) => s.toLowerCase();
    const filtered = FLIGHTS.filter(
      (f) =>
        term(f.fromCity).includes(term(q.from)) ||
        term(f.from).includes(term(q.from)) ||
        term(f.toCity).includes(term(q.to)) ||
        term(f.to).includes(term(q.to))
    );
    setResults(filtered.length ? filtered : FLIGHTS);
    toast({
      title: `${filtered.length || FLIGHTS.length} flights found`,
      description: `${q.from || "Anywhere"} → ${q.to || "Anywhere"} on ${q.date}`,
    });
  };

  const swap = () => setQ({ ...q, from: q.to, to: q.from });

  const closeDialog = () => {
    setSelected(null);
    setConfirmed(null);
    setTraveller({ name: "", phone: "", email: "" });
  };

  const book = (e) => {
    e.preventDefault();
    if (!traveller.name || !traveller.phone) {
      toast({ title: "Details required", description: "Please enter name & phone." });
      return;
    }
    const pnr = "PMRF" + Math.random().toString(36).substring(2, 7).toUpperCase();
    setConfirmed({ pnr, data: selected });
  };

  return (
    <section id="flights" className="py-20 md:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold tracking-wider uppercase">
            <Plane className="w-3.5 h-3.5" /> Flight Booking
          </div>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Fly with the best fares
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Compare 500+ domestic & international airlines. Zero convenience fee on IndiGo, Air India and Vistara.
          </p>
        </div>

        <div className="mt-10 bg-white rounded-2xl shadow-xl shadow-slate-900/5 border border-slate-100 p-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { id: "oneway", label: "One Way" },
              { id: "round", label: "Round Trip" },
              { id: "multi", label: "Multi-City" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setQ({ ...q, trip: t.id })}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition ${
                  q.trip === t.id
                    ? "bg-teal-600 text-white border-teal-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <form onSubmit={search} className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_1fr_auto_auto] gap-2 items-center">
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50">
              <MapPin className="w-5 h-5 text-teal-600" />
              <div className="flex-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">From</label>
                <Input
                  value={q.from}
                  onChange={(e) => setQ({ ...q, from: e.target.value })}
                  placeholder="Delhi (DEL)"
                  className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900 placeholder:text-slate-400"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={swap}
              className="hidden md:flex w-10 h-10 rounded-full bg-slate-100 hover:bg-teal-600 hover:text-white items-center justify-center text-slate-600 transition"
              aria-label="swap"
            >
              <ArrowLeftRight className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
              <MapPin className="w-5 h-5 text-orange-500" />
              <div className="flex-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">To</label>
                <Input
                  value={q.to}
                  onChange={(e) => setQ({ ...q, to: e.target.value })}
                  placeholder="Goa (GOI)"
                  className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900 placeholder:text-slate-400"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
              <Calendar className="w-5 h-5 text-teal-600" />
              <div className="flex-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Depart</label>
                <Input
                  type="date"
                  value={q.date}
                  onChange={(e) => setQ({ ...q, date: e.target.value })}
                  className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
              <Users className="w-5 h-5 text-teal-600" />
              <div className="flex-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Travellers</label>
                <Input
                  type="number"
                  min="1"
                  max="9"
                  value={q.pax}
                  onChange={(e) => setQ({ ...q, pax: e.target.value })}
                  className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900"
                />
              </div>
            </div>
            <Button type="submit" className="h-14 md:h-auto md:aspect-square md:w-14 bg-teal-600 hover:bg-teal-700 rounded-xl">
              <Search className="w-5 h-5" />
              <span className="md:hidden ml-2">Search Flights</span>
            </Button>
          </form>
        </div>

        <div className="mt-10">
          <div className="flex items-end justify-between mb-4">
            <h3 className="text-xl font-bold text-slate-900">Available Flights</h3>
            <span className="text-sm text-slate-500">{results.length} results</span>
          </div>
          <div className="space-y-3">
            {results.map((f) => (
              <div
                key={f.id}
                className="group bg-white rounded-xl border border-slate-200 hover:border-teal-500 hover:shadow-lg transition p-5 grid grid-cols-1 md:grid-cols-[auto_1fr_auto_auto] gap-4 md:gap-6 items-center"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-lg flex items-center justify-center font-bold text-sm ${airlineColor[f.logo] || "bg-slate-100 text-slate-700"}`}>
                    {f.logo}
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">{f.airline}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{f.code}</div>
                  </div>
                </div>

                <div className="flex items-center gap-4 md:gap-6">
                  <div>
                    <div className="text-xl font-bold text-slate-900 leading-none">{f.dep}</div>
                    <div className="text-xs text-slate-500 mt-1">{f.from} · {f.fromCity}</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center min-w-[110px]">
                    <div className="text-[11px] text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {f.duration}
                    </div>
                    <div className="w-full h-px bg-slate-200 relative my-1">
                      <Plane className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 text-teal-600 rotate-90" />
                    </div>
                    <div className="text-[11px] text-teal-700 font-semibold">{f.stops}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-slate-900 leading-none">{f.arr}</div>
                    <div className="text-xs text-slate-500 mt-1">{f.to} · {f.toCity}</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1 justify-end">
                  <Badge variant="outline" className="gap-1 text-[10px] font-medium border-slate-200 text-slate-600">
                    <Luggage className="w-2.5 h-2.5" /> 15kg
                  </Badge>
                  <Badge variant="outline" className="gap-1 text-[10px] font-medium border-slate-200 text-slate-600">
                    <Utensils className="w-2.5 h-2.5" /> Meal
                  </Badge>
                  {f.refundable && (
                    <Badge variant="outline" className="text-[10px] font-medium border-green-200 text-green-700 bg-green-50">
                      Refundable
                    </Badge>
                  )}
                </div>

                <div className="text-right">
                  <div className="text-[11px] text-slate-500">from</div>
                  <div className="text-xl font-bold text-teal-700">₹{f.price.toLocaleString("en-IN")}</div>
                  <Button
                    onClick={() => setSelected(f)}
                    className="mt-2 bg-orange-500 hover:bg-orange-600 text-white h-9"
                  >
                    Book <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && closeDialog()}>
        <DialogContent className="max-w-lg">
          {selected && !confirmed && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Book Flight
                </DialogTitle>
                <DialogDescription>
                  {selected.airline} {selected.code} · {selected.fromCity} ({selected.from}) → {selected.toCity} ({selected.to}) · {selected.dep}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={book} className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="fname" className="text-xs">Full Name (as on ID) *</Label>
                  <Input id="fname" value={traveller.name} onChange={(e) => setTraveller({ ...traveller, name: e.target.value })} placeholder="Ravi Kumar" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="fphone" className="text-xs">Phone *</Label>
                    <Input id="fphone" value={traveller.phone} onChange={(e) => setTraveller({ ...traveller, phone: e.target.value })} placeholder="+91 98765 43210" />
                  </div>
                  <div>
                    <Label htmlFor="femail" className="text-xs">Email</Label>
                    <Input id="femail" type="email" value={traveller.email} onChange={(e) => setTraveller({ ...traveller, email: e.target.value })} placeholder="you@example.com" />
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-100 flex items-end justify-between">
                  <div>
                    <div className="text-xs text-slate-500">Total ({q.pax} traveller{q.pax > 1 ? "s" : ""})</div>
                    <div className="text-2xl font-bold text-slate-900">₹{(selected.price * (q.pax || 1)).toLocaleString("en-IN")}</div>
                    <div className="text-[10px] text-slate-500">Incl. taxes & fees</div>
                  </div>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700">Confirm & Pay</Button>
                </div>
              </form>
            </>
          )}

          {confirmed && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 mx-auto flex items-center justify-center">
                <Plane className="w-9 h-9 text-green-600" />
              </div>
              <h3 className="mt-5 text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Flight Booked!
              </h3>
              <p className="mt-2 text-slate-600 text-sm">
                Your e-ticket has been emailed & SMS-ed. Check in 24 hours before departure.
              </p>
              <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-left space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">PNR</span><span className="font-mono font-semibold">{confirmed.pnr}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Flight</span><span className="font-semibold">{confirmed.data.airline} {confirmed.data.code}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Route</span><span className="font-semibold">{confirmed.data.from} → {confirmed.data.to}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Traveller</span><span className="font-semibold">{traveller.name}</span></div>
              </div>
              <Button onClick={closeDialog} className="mt-6 bg-teal-600 hover:bg-teal-700">Done</Button>
              <p className="mt-4 text-[11px] text-orange-600 font-semibold">DEMO MODE — Flight booking is MOCKED (no real transaction).</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default FlightSearch;
